import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { inr } from "@/lib/brand";
import { CouponFormDialog } from "@/components/admin/CouponFormDialog";

const fmtD = (iso) => (iso ? iso.split("-").reverse().join("/") : "No expiry");
const valueText = (c) => (c.kind === "flat" ? inr(c.value) : `${c.value}%${c.max_discount ? ` (max ${inr(c.max_discount)})` : ""}`);

function CouponRow({ c, onEdit, onDelete }) {
  return (
    <tr data-testid={`coupon-row-${c.code}`}>
      <td className="px-4 py-3 font-mono font-semibold">{c.code}</td>
      <td className="px-4 py-3">{valueText(c)}{c.first_trip_only && <span className="ml-2 text-xs text-neutral-500">first trip</span>}</td>
      <td className="px-4 py-3 text-xs text-neutral-400">{c.min_order ? `Min ${inr(c.min_order)}` : "—"}</td>
      <td className="px-4 py-3 text-xs">{fmtD(c.expires_on)}</td>
      <td className="px-4 py-3 text-xs">{c.used_count || 0}{c.usage_limit ? ` / ${c.usage_limit}` : ""}</td>
      <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${c.active ? "bg-emerald-500/15 text-emerald-400" : "bg-white/10 text-neutral-400"}`}>{c.active ? "Active" : "Off"}</span></td>
      <td className="px-4 py-3 text-right">
        <button onClick={() => onEdit(c)} data-testid={`coupon-edit-${c.code}`} className="rounded p-1.5 hover:bg-white/10" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
        <button onClick={() => onDelete(c)} data-testid={`coupon-delete-${c.code}`} className="rounded p-1.5 text-rose-400 hover:bg-white/10" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
      </td>
    </tr>
  );
}

export default function AdminCoupons() {
  const api = useApi();
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(undefined);
  const load = useCallback(() => {
    api.get("/admin/coupons").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [api]);
  useEffect(() => {
    load();
  }, [load]);
  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try {
      const r = await api.delete(`/admin/coupons/${c.code}`);
      toast.success(r.data.deactivated ? "Coupon was already used, so it has been switched off" : "Coupon deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="admin-coupons">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Coupons &amp; referrals</h1>
          <p className="mt-1 text-sm text-neutral-400">Referrals run automatically: a friend gets ₹300 off their first trip, and the referrer gets a ₹300 code once that trip is completed.</p>
        </div>
        <button onClick={() => setEdit(null)} data-testid="coupon-add" className="flex items-center gap-2 rounded-md bg-[#FF5A00] px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" />New coupon</button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-white/10 bg-[#141414]">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>{["Code", "Discount", "Minimum", "Expires", "Used", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {list.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-500" data-testid="coupons-empty">No coupons yet.</td></tr>}
            {list.map((c) => <CouponRow key={c.code} c={c} onEdit={setEdit} onDelete={remove} />)}
          </tbody>
        </table>
      </div>
      <CouponFormDialog coupon={edit} onClose={() => setEdit(undefined)} onSaved={load} />
    </div>
  );
}
